import { LineChart } from '@wundo/ui';
import type { MetricDataPoint } from '@/lib/dashboard.ts';
import { ChartCard } from './ChartCard.tsx';
import { snapshotDate } from './snapshotDates.ts';

export interface MetricChartCardProps {
  title: string;
  dataPoints: MetricDataPoint[];
  /** Denominator for the percentage beside the latest value. */
  percentOf?: number | null;
  /** One quiet line under the title: what the chart counts. */
  description?: string;
  /**
   * Activated users, for the retention line under the chart. Every figure on the page
   * divides by all provisioned users, so this ratio needs to name its own denominator.
   */
  retentionOf?: number | null;
  info?: string;
}

/** One metric's daily snapshots as a line, with where it stands today. */
export function MetricChartCard({
  title,
  dataPoints,
  percentOf = null,
  description,
  retentionOf = null,
  info,
}: MetricChartCardProps) {
  const latest = dataPoints[dataPoints.length - 1];
  const percent =
    latest !== undefined && percentOf
      ? Math.round((latest.value / percentOf) * 100)
      : null;
  const retentionPercent =
    latest !== undefined && retentionOf
      ? Math.round((latest.value / retentionOf) * 100)
      : null;

  return (
    <ChartCard
      title={title}
      info={info}
      description={description}
      aside={
        latest !== undefined && (
          <>
            <p className='text-xl font-semibold text-ink tabular-nums'>
              {latest.value}
              {percent !== null && (
                <span className='text-md font-medium text-ink-muted'>{` · ${percent}%`}</span>
              )}
            </p>
            <p className='text-xs text-ink-muted'>
              as of {snapshotDate(latest.period_end)}
            </p>
          </>
        )
      }
    >
      <LineChart
        ariaLabel={title}
        labels={dataPoints.map((point) => snapshotDate(point.period_end))}
        series={[{ label: title, data: dataPoints.map((point) => point.value) }]}
        height={200}
      />
      {retentionPercent !== null && latest !== undefined && (
        <p className='text-xs text-ink-muted'>
          {`Retention: ${latest.value} of ${retentionOf} activated users (${retentionPercent}%)`}
        </p>
      )}
    </ChartCard>
  );
}
